import {
  ChangePasswordRequest,
  ChangePasswordResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  GetAccessTokenRequest,
  GetAccessTokenResponse,
  GetCurrentUserResponse,
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ResetTempPasswordRequest,
  ResetTempPasswordResponse,
  SignupRequest,
  SignupResponse,
} from '@customTypes/auth/Auth';
import {
  CheckCompanyRequest,
  CheckCompanyResponse,
} from '@customTypes/company';
import type {
  InviteUserRequest,
  InviteUserResponse,
  ResendInviteRequest,
  ResendInviteResponse,
} from '@customTypes/user/User';
import { formatError } from '@utils/FormatError';
import ApiService from './ApiService';

class AuthService {
  // Create Users
  public async signup(requestPayload: SignupRequest): Promise<SignupResponse> {
    const response = await ApiService.post<SignupRequest, SignupResponse>(
      `/auth/signup`,
      requestPayload,
    );

    if (response.success && response.data) {
      return response.data || {};
    } else {
      throw new Error(response?.error || 'Failed to signup');
    }
  }

  /**
   * Invite staff user under a admin user
   *
   * @param requestPayload<InviteUserRequest>
   * @returns
   */
  public async inviteStaffUser(
    requestPayload: InviteUserRequest,
  ): Promise<InviteUserResponse> {
    const { success, data, error } = await ApiService.post<
      InviteUserRequest,
      InviteUserResponse
    >(`/auth/create-admin-user`, requestPayload);

    if (success) {
      return data as InviteUserResponse;
    } else {
      throw new Error(formatError(error) || 'Failed to invite staff user.');
    }
  }

  /**
   * Resent Invite
   *
   * @param payload<ResendInviteRequest>
   * @returns
   */
  public async resendInvite(
    payload: ResendInviteRequest,
  ): Promise<ResendInviteResponse> {
    const { success, data, error } = await ApiService.post<
      ResendInviteRequest,
      ResendInviteResponse
    >(`/auth/resend-invite`, payload);
    if (success) {
      return data as ResendInviteResponse;
    } else {
      throw new Error(formatError(error) || 'Failed to resend invite.');
    }
  }

  /**
   * Reset staff user's Temporary password
   *
   * @param requestPayload<ResetTempPasswordRequest>
   * @returns
   */
  public async resetTempPassword(
    requestPayload: ResetTempPasswordRequest,
  ): Promise<ResetTempPasswordResponse> {
    const { success, data, error } = await ApiService.post<
      ResetTempPasswordRequest,
      ResetTempPasswordResponse
    >(`/auth/reset-temp-password`, requestPayload);

    if (success) {
      return data as ResetTempPasswordResponse;
    } else {
      throw new Error(
        formatError(error) || 'Failed to reset password of staff user.',
      );
    }
  }

  // Verify User
  public async verifyUser(requestPayload: any): Promise<any> {
    const response = await ApiService.post(`/auth/verify-user`, requestPayload);

    if (response.success && response.data) {
      return response.data || {};
    } else {
      throw new Error(
        response.error || 'Failed to verify code or invalid code',
      );
    }
  }

  /**
   * Resend sign-up confirmation code to the user's email.
   */
  public async resendSignupConfirmationCode(
    username: string,
  ): Promise<{ message?: string }> {
    const response = await ApiService.post<
      { username: string },
      { message?: string }
    >(`/auth/resend-confirmation-code`, { username });

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(
      response.error || 'Failed to resend confirmation code',
    );
  }

  // Login User
  public async login(requestPayload: LoginRequest): Promise<LoginResponse> {
    const response = await ApiService.post<LoginRequest, LoginResponse>(
      `/auth/login`,
      requestPayload,
    );

    if (response.success) {
      return response.data as LoginResponse;
    }
    const err = new Error(
      response.error || 'Failed to login user, please try again later',
    ) as Error & { code?: string; username?: string };
    if (response.errorData) {
      err.code = response.errorData.code as string | undefined;
      err.username = response.errorData.username as string | undefined;
    }
    throw err;
  }

  public async refreshToken(): Promise<void> {
    const response = await ApiService.post(
      `/auth/refresh`,
      {}
    );
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to refresh token');
    }
  }

  // Logout
  public async logout(): Promise<void> {
    await ApiService.post(`/auth/logout`, {});
  }

  // Get cognito AccessToken
  public async getAccessToken(
    requestPayload: GetAccessTokenRequest,
  ): Promise<GetAccessTokenResponse> {
    const response = await ApiService.post<
      GetAccessTokenRequest,
      GetAccessTokenResponse
    >(`/auth/callback`, requestPayload);

    if (response.success && response.data) {
      return response.data;
    } else {
      throw new Error(response.error || 'Failed to getAccessToken');
    }
  }

  /**
   * Convert anonymous demo session to the current (newly registered) user.
   * Requires the user to be logged in (Cognito) and an anon_access_token cookie
   * from the demo. Transfers company data to the new account.
   */
  public async convertAnonymous(): Promise<{ converted: boolean; company_name?: string }> {
    const response = await ApiService.post<Record<string, never>, { converted: boolean; company_name?: string }>(
      `/auth/convert-anonymous`,
      {},
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to convert demo session');
  }

  public async getCurrentUser(
    includes: string = '',
  ): Promise<GetCurrentUserResponse> {
    const response = await ApiService.get<GetCurrentUserResponse>(`/users/me`, {
      includes,
    });

    if (response.success && response.data) {
      return response.data;
    } else {
      throw new Error(response.error || 'Failed to getCurrentUser');
    }
  }

  public async checkCompany(
    company_name: string,
  ): Promise<CheckCompanyResponse> {
    const response = await ApiService.post<
      CheckCompanyRequest,
      CheckCompanyResponse
    >(`/auth/check-company`, { company_name });
    if (response.success && response.data) {
      return response.data;
    } else {
      throw new Error(response.error || 'Failed to checkCompany');
    }
  }

  // Forgot password
  public async forgotPassword(
    requestPayload: ForgotPasswordRequest,
  ): Promise<ForgotPasswordResponse> {
    const response = await ApiService.post<
      ForgotPasswordRequest,
      ForgotPasswordResponse
    >(`/auth/forgot-password`, requestPayload);

    if (response.success && response.data) {
      return response.data;
    } else {
      throw new Error(
        response.error || 'Failed to forgot password, please try again later',
      );
    }
  }

  // Reset password
  public async resetPassword(
    requestPayload: ResetPasswordRequest,
  ): Promise<ResetPasswordResponse> {
    const response = await ApiService.post<
      ResetPasswordRequest,
      ResetPasswordResponse
    >(`/auth/reset-password`, requestPayload);

    if (response.success && response.data) {
      return response.data;
    } else {
      throw new Error(
        response.error || 'Failed to reset password, please try again later',
      );
    }
  }

  // Change password
  public async changePassword(
    requestPayload: ChangePasswordRequest,
  ): Promise<ChangePasswordResponse> {
    const response = await ApiService.post<
      ChangePasswordRequest,
      ChangePasswordResponse
    >(`/auth/change-password`, requestPayload);

    if (response.success && response.data) {
      return response.data;
    } else {
      throw new Error(
        response.error || 'Failed to change password, please try again later',
      );
    }
  }
}

export default new AuthService();
